import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Bell,
  CalendarDays,
  CheckSquare,
  ChevronDown,
  ChevronLeft,
  CreditCard,
  FilePen,
  FileText,
  Image,
  Inbox,
  LayoutDashboard,
  Link2,
  LogOut,
  PanelLeftOpen,
  Plus,
  Receipt,
  ScrollText,
  Settings,
  UsersRound,
  Users,
  X,
} from 'lucide-react';

import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../context/LanguageContext';
import { usePageHeaderValue } from '../../context/PageHeaderContext';
import { LanguageToggle } from '../core/LanguageToggle';
import { RenewalReminderBanner } from '../subscription/RenewalReminderBanner';
import { useRenewalReminder } from '../../hooks/useRenewalReminder';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { PLAN_NAME_KEYS } from '../../i18n';
import type { TranslationKey } from '../../i18n';
import {
  getDashboardTabPath,
  getNavTabFor,
  resolveDashboardTab,
} from '../../constants/dashboard';
import type { DashboardTab } from '../../constants/dashboard';
import { daysUntil } from '../../utils/format';
import type { UserSubscription } from '../../api/subscription.api';

interface NavEntry {
  id: DashboardTab;
  labelKey: TranslationKey;
  icon: React.ComponentType<{ className?: string }>;
  /** Unread/pending count shown as a pill on the right of the row. */
  badge?: number;
}

/** The primary nav, in the order the design lays it out. */
const PRIMARY_NAV: NavEntry[] = [
  { id: 'overview', labelKey: 'nav.overview', icon: LayoutDashboard },
  { id: 'calendar', labelKey: 'nav.calendar', icon: CalendarDays },
  { id: 'posts', labelKey: 'nav.posts', icon: FileText },
  { id: 'drafts', labelKey: 'nav.drafts', icon: FilePen },
  { id: 'library', labelKey: 'nav.library', icon: Image },
  { id: 'analytics', labelKey: 'nav.analytics', icon: BarChart3 },
  { id: 'inbox', labelKey: 'nav.inbox', icon: Inbox, badge: 4 },
  { id: 'accounts', labelKey: 'nav.accounts', icon: Link2 },
  { id: 'team', labelKey: 'nav.team', icon: UsersRound },
  { id: 'settings', labelKey: 'nav.settings', icon: Settings },
];

/**
 * Billing and approvals are not in the redesign's sidebar, but they are built
 * and reachable. Dropping their entries would strand working features behind
 * URLs nobody can find, so they sit below a rule instead.
 */
const SECONDARY_NAV: NavEntry[] = [
  { id: 'approvals', labelKey: 'nav.approvals', icon: CheckSquare },
  { id: 'subscription', labelKey: 'nav.subscription', icon: CreditCard },
  { id: 'payments', labelKey: 'nav.payments', icon: Receipt },
];

/** Appended only for superadmins — the platform operator, not a tenant role. */
const ADMIN_NAV: NavEntry[] = [
  { id: 'admin-users', labelKey: 'nav.adminUsers', icon: Users },
  { id: 'admin-activity', labelKey: 'nav.adminActivity', icon: ScrollText },
  { id: 'admin-stats', labelKey: 'nav.adminStats', icon: BarChart3 },
];

const ALL_NAV = [...PRIMARY_NAV, ...SECONDARY_NAV, ...ADMIN_NAV];

/** Mock until the notifications feature exists. */
const UNREAD_NOTIFICATIONS = 3;

interface DashboardShellProps {
  subscription: UserSubscription | null;
  children: React.ReactNode;
}

/**
 * The chrome every dashboard tab renders inside: sidebar, header, renewal
 * banner. The page fills the header's title and action buttons through
 * `PageHeaderContext`; tabs that do not set one fall back to their nav label.
 */
export function DashboardShell({ subscription, children }: Readonly<DashboardShellProps>) {
  const { signOut, user, isSuperAdmin } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const pageHeader = usePageHeaderValue();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [planCardVisible, setPlanCardVisible] = useState(true);

  // The URL owns the active tab, so it is derived rather than mirrored in state.
  const activeTab = resolveDashboardTab(pathname);
  const activeNavTab = getNavTabFor(activeTab);
  const go = useCallback(
    (tab: DashboardTab) => navigate(getDashboardTabPath(tab)),
    [navigate]
  );

  const renewalReminder = useRenewalReminder(subscription);
  const daysRemaining = subscription ? daysUntil(subscription.periodEnd) : 0;
  const planName = subscription ? t(PLAN_NAME_KEYS[subscription.plan]) : t('common.emptyValue');

  const secondaryNav = useMemo(
    () => (isSuperAdmin ? [...SECONDARY_NAV, ...ADMIN_NAV] : SECONDARY_NAV),
    [isSuperAdmin]
  );

  const fallbackTitleKey = ALL_NAV.find((entry) => entry.id === activeNavTab)?.labelKey;
  const headerTitle = pageHeader?.title ?? (fallbackTitleKey ? t(fallbackTitleKey) : t('common.dashboard'));

  const renderNavRow = (entry: NavEntry) => {
    const Icon = entry.icon;
    const isActive = activeNavTab === entry.id;
    return (
      <button
        key={entry.id}
        type="button"
        onClick={() => go(entry.id)}
        aria-current={isActive ? 'page' : undefined}
        className={cn(
          'group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-[13px] transition-colors',
          isActive
            ? 'bg-blue-50 font-semibold text-blue-700'
            : 'font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900'
        )}
      >
        <Icon className={cn('h-4 w-4 shrink-0', isActive ? 'text-blue-600' : 'text-slate-400')} />
        <span className="flex-1 truncate">{t(entry.labelKey)}</span>
        {entry.badge !== undefined && entry.badge > 0 && (
          <span className="shrink-0 rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-600 group-hover:bg-slate-300">
            {entry.badge}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-100">
      {sidebarOpen && (
        <aside className="z-10 flex h-full w-[232px] shrink-0 flex-col border-r border-slate-200 bg-white">
          {/* Wordmark */}
          <div className="flex h-[60px] shrink-0 items-center gap-2.5 px-5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600 text-[15px] font-black text-white">
              P
            </div>
            <span className="text-[17px] font-bold tracking-tight text-slate-900">
              {t('common.brand')}
            </span>
          </div>

          {/* Primary action */}
          <div className="px-4 pb-3">
            <button
              type="button"
              onClick={() => go('composer')}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              {t('nav.createPost')}
            </button>
          </div>

          <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 pb-3">
            {PRIMARY_NAV.map(renderNavRow)}

            <div className="!mt-3 border-t border-slate-100 pt-3">
              {secondaryNav.map(renderNavRow)}
            </div>
          </nav>

          {/* Workspace + plan + collapse */}
          <div className="shrink-0 space-y-2.5 border-t border-slate-100 px-4 py-3">
            <WorkspaceSwitcher />

            {planCardVisible && (
              <div className="relative rounded-xl bg-blue-50 px-3 py-2.5">
                <button
                  type="button"
                  onClick={() => setPlanCardVisible(false)}
                  aria-label={t('common.dismiss')}
                  className="absolute right-2 top-2 text-slate-400 transition-colors hover:text-slate-600"
                >
                  <X className="h-3 w-3" />
                </button>
                <p className="text-[13px] font-bold text-blue-700">{planName}</p>
                <p className="mt-0.5 text-[11px] text-slate-500">
                  {daysRemaining > 0
                    ? t('subscription.renewsInDays', { days: daysRemaining })
                    : t('common.expired')}
                </p>
                <button
                  type="button"
                  onClick={() => go('subscription')}
                  className="mt-2 w-full rounded-lg border border-blue-200 bg-white py-1.5 text-[12px] font-semibold text-blue-700 transition-colors hover:bg-blue-100"
                >
                  {t('subscription.upgrade')}
                </button>
              </div>
            )}

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center gap-1.5 text-[12px] font-medium text-slate-400 transition-colors hover:text-slate-700"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                {t('nav.collapse')}
              </button>
              <LanguageToggle variant="segmented" />
            </div>
          </div>
        </aside>
      )}

      <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-[60px] shrink-0 items-center gap-4 border-b border-slate-200 bg-white px-6">
          {!sidebarOpen && (
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              aria-label={t('nav.expand')}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-blue-600"
            >
              <PanelLeftOpen className="h-4 w-4" />
            </button>
          )}

          <div className="min-w-0 flex-1">
            <h1 className="truncate text-[17px] font-bold leading-tight text-slate-900">
              {headerTitle}
            </h1>
            {pageHeader?.subtitle && (
              <p className="truncate text-[12px] leading-tight text-slate-500">
                {pageHeader.subtitle}
              </p>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-3">
            <button
              type="button"
              aria-label={t('nav.notifications')}
              className="relative flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
            >
              <Bell className="h-[18px] w-[18px]" />
              <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">
                {UNREAD_NOTIFICATIONS}
              </span>
            </button>

            <AccountMenu email={user?.email ?? null} onSignOut={() => void signOut()} />
          </div>
        </header>

        {/* Between the header and the scroll container so it stays put on every
            tab: an expired account cannot publish anything, so this is worth the
            interruption until it is dismissed or acted on. */}
        {renewalReminder.visible && <RenewalReminderBanner onDismiss={renewalReminder.dismiss} />}

        <div className="flex-1 overflow-auto p-4 sm:p-5">{children}</div>
      </main>
    </div>
  );
}

/** Avatar + name in the header, with a sign-out menu behind it. */
function AccountMenu({
  email,
  onSignOut,
}: Readonly<{ email: string | null; onSignOut: () => void }>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const displayName = email?.split('@')[0] ?? t('common.emptyValue');
  const initials = displayName.slice(0, 2).toUpperCase();

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex items-center gap-2 rounded-lg py-1 pl-1 pr-2 transition-colors hover:bg-slate-100"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-[11px] font-bold text-white">
          {initials}
        </span>
        <span className="hidden max-w-[120px] truncate text-[13px] font-semibold capitalize text-slate-700 sm:block">
          {displayName}
        </span>
        <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-30 mt-2 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg shadow-slate-900/10"
        >
          <div className="border-b border-slate-100 px-3 py-2">
            <p className="truncate text-[13px] font-semibold capitalize text-slate-800">
              {displayName}
            </p>
            <p className="truncate text-[11px] text-slate-400">{email}</p>
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={onSignOut}
            className="flex w-full items-center gap-2 px-3 py-2 text-[13px] font-medium text-slate-600 transition-colors hover:bg-rose-50 hover:text-rose-600"
          >
            <LogOut className="h-4 w-4" />
            {t('common.signOut')}
          </button>
        </div>
      )}
    </div>
  );
}
